import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Page } from '../../components/shell/Page';
import SplitLayout from '../../components/shell/SidePane';
import DataGrid from '../../components/datagrid/DataGrid';
import { Button } from '../../components/ui/button';
import {
  Certificate,
  Eye,
  GraduationCap,
  DownloadSimple
} from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function LearnerCertificates() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCert, setSelectedCert] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  useEffect(() => {
    fetchCertificates();
  }, []);

  const fetchCertificates = async () => {
    try {
      const res = await api.get('/certificates');
      setCertificates(res.data);
    } catch (error) {
      toast.error('Failed to load certificates');
    } finally {
      setLoading(false);
    }
  };

  // Download certificate PDF
  const handleDownloadPdf = async (cert) => {
    setDownloadingPdf(true);
    try {
      const response = await api.get(`/certificates/${cert.id}/download`, {
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `certificate_${cert.certificate_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Certificate downloaded!');
    } catch (error) {
      toast.error('Failed to download certificate');
    } finally {
      setDownloadingPdf(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-8 flex items-center justify-center min-h-[60vh]">
          <div className="animate-pulse">Loading certificates...</div>
        </div>
      </DashboardLayout>
    );
  }

  // ─── Certificate preview — side pane (no popup) ───
  const closePane = () => setSelectedCert(null);
  const side = selectedCert ? {
    open: true,
    onOpenChange: closePane,
    title: 'Certificate of Completion',
    subtitle: selectedCert.course_title,
    icon: Certificate,
    testid: 'certificate-pane',
    children: (
      <div className="p-6 bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 rounded-xl border-2 border-emerald-200 dark:border-emerald-800" data-testid="certificate-preview">
        <div className="text-center space-y-4">
          <div className="w-20 h-20 mx-auto bg-emerald-100 dark:bg-emerald-900/50 rounded-full flex items-center justify-center">
            <Certificate className="w-10 h-10 text-emerald-600" weight="duotone" />
          </div>

          <div>
            <p className="text-sm text-emerald-600 dark:text-emerald-400 font-medium uppercase tracking-wider">Certificate of Completion</p>
            <h2 className="text-2xl font-heading font-bold mt-2">{selectedCert.course_title}</h2>
          </div>

          <div className="py-4 border-y border-emerald-200 dark:border-emerald-800">
            <p className="text-sm text-muted-foreground">This certifies that</p>
            <p className="text-xl font-semibold mt-1">{selectedCert.user_name}</p>
            <p className="text-sm text-muted-foreground mt-2">has successfully completed this course</p>
          </div>

          <div className="grid grid-cols-1 2xl:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">Issue Date</p>
              <p className="font-medium">{new Date(selectedCert.issued_at).toLocaleDateString()}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Certificate ID</p>
              <p className="font-mono text-xs break-all">{selectedCert.certificate_number}</p>
            </div>
          </div>

          {selectedCert.exam_score !== null && selectedCert.exam_score !== undefined && (
            <div className="mt-4 p-3 bg-white dark:bg-black/20 rounded-lg">
              <p className="text-sm text-muted-foreground">Exam Score</p>
              <p className="text-2xl font-bold text-emerald-600">{selectedCert.exam_score}%</p>
            </div>
          )}
        </div>
      </div>
    ),
    footer: (
      <div className="flex items-center gap-2">
        <div className="ml-auto flex items-center gap-2">
          <Button type="button" variant="outline" size="sm" className="h-9" onClick={closePane}>Close</Button>
          <Button
            type="button"
            size="sm"
            className="h-9"
            onClick={() => handleDownloadPdf(selectedCert)}
            disabled={downloadingPdf}
            data-testid="download-certificate-btn"
          >
            <DownloadSimple className="w-4 h-4" />
            {downloadingPdf ? 'Downloading...' : 'Download PDF'}
          </Button>
        </div>
      </div>
    ),
  } : null;

  return (
    <DashboardLayout>
      <SplitLayout side={side}>
      <Page data-testid="learner-certificates">
        <DataGrid
          title="My certificates"
          rows={certificates}
          loading={loading}
          onRefresh={fetchCertificates}
          searchKeys={['user_name']}
          columns={[
            { key: 'certificate_number', header: 'Certificate no.', width: 200, className: 'font-mono text-xs' },
            { key: 'course_title', header: 'Course', className: 'font-medium' },
            { key: 'issued_at', header: 'Issued', width: 130, render: (c) => new Date(c.issued_at).toLocaleDateString() },
            {
              key: 'exam_score', header: 'Completion', width: 120, align: 'right',
              accessor: (c) => (c.exam_score === null || c.exam_score === undefined ? null : Number(c.exam_score)),
              render: (c) => (c.exam_score === null || c.exam_score === undefined ? '—' : `${c.exam_score}%`),
            },
          ]}
          onRowDoubleClick={(c) => handleDownloadPdf(c)}
          primaryActions={(
            <Button size="sm" variant="outline" className="h-9" onClick={() => navigate('/learn/catalog')}>
              <GraduationCap className="w-4 h-4" /> Browse courses
            </Button>
          )}
          selectionActions={[
            { key: 'download', label: 'Download PDF', icon: DownloadSimple, onClick: ([c]) => handleDownloadPdf(c), disabled: downloadingPdf },
            { key: 'view', label: 'View', icon: Eye, onClick: ([c]) => setSelectedCert(c) },
          ]}
          emptyMessage="No certificates yet. Complete courses to earn certificates of completion."
        />
      </Page>
      </SplitLayout>
    </DashboardLayout>
  );
}
